import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { registerHealthCallbacks, unregisterHealthCallbacks } from '../services/resilientFetch';
import {
  showDegradedToast,
  showOfflineToast,
  showRecoveryToast,
  showRateLimitedToast,
  showQuotaWarningToast,
} from '../utils/apiToasts';
import type { ServiceId, ServiceStatus, ServiceHealth, RateLimitInfo } from '../types';

const DEGRADED_THRESHOLD = 3;
const OFFLINE_THRESHOLD = 10;
const QUOTA_WARNING_THRESHOLD = 60;
const QUOTA_CRITICAL_THRESHOLD = 80;

const SERVICE_NAMES: Record<ServiceId, string> = {
  genaiops: 'GenAIOps',
  core: 'Core API',
};

type ServicesState = Record<ServiceId, ServiceHealth>;
type RateLimitsState = Record<ServiceId, RateLimitInfo>;

interface ApiHealthContextValue {
  services: ServicesState;
  isAnyServiceDegraded: boolean;
  isAnyServiceOffline: boolean;
  rateLimits: RateLimitsState;
  getQuotaPercentage: (serviceId: ServiceId) => number | null;
  QUOTA_WARNING_THRESHOLD: number;
  QUOTA_CRITICAL_THRESHOLD: number;
  resetServiceHealth: (serviceId: ServiceId) => void;
}

const ApiHealthContext = createContext<ApiHealthContextValue | null>(null);

export const useApiHealth = (): ApiHealthContextValue => {
  const context = useContext(ApiHealthContext);
  if (!context) {
    throw new Error('useApiHealth must be used within an ApiHealthProvider');
  }
  return context;
};

const initialServiceHealth: ServiceHealth = {
  status: 'healthy',
  consecutiveFailures: 0,
  lastError: null,
  lastErrorTime: null,
};

const initialRateLimitInfo: RateLimitInfo = {
  limitMinute: null,
  remainingMinute: null,
  resetMinute: null,
  limitHour: null,
  remainingHour: null,
  resetHour: null,
};

export const ApiHealthProvider = ({ children }: { children: ReactNode }) => {
  const [services, setServices] = useState<ServicesState>({
    genaiops: { ...initialServiceHealth },
    core: { ...initialServiceHealth },
  });

  const [rateLimits, setRateLimits] = useState<RateLimitsState>({
    genaiops: { ...initialRateLimitInfo },
    core: { ...initialRateLimitInfo },
  });

  const quotaWarningShown = useRef<Record<ServiceId, boolean>>({ genaiops: false, core: false });
  const previousStatus = useRef<Record<ServiceId, ServiceStatus>>({ genaiops: 'healthy', core: 'healthy' });

  const getQuotaPercentage = useCallback((serviceId: ServiceId): number | null => {
    const limits = rateLimits[serviceId];
    if (limits?.limitMinute == null || limits?.remainingMinute == null) {
      return null;
    }
    const used = limits.limitMinute - limits.remainingMinute;
    return Math.round((used / limits.limitMinute) * 100);
  }, [rateLimits]);

  const handleSuccess = useCallback((serviceId: ServiceId, _rateLimitInfo?: RateLimitInfo) => {
    setServices((prev) => {
      const service = prev[serviceId];
      const wasNotHealthy = service.status !== 'healthy';

      if (wasNotHealthy && previousStatus.current[serviceId] !== 'healthy') {
        showRecoveryToast(SERVICE_NAMES[serviceId]);
      }

      previousStatus.current[serviceId] = 'healthy';

      return {
        ...prev,
        [serviceId]: {
          status: 'healthy' as ServiceStatus,
          consecutiveFailures: 0,
          lastError: null,
          lastErrorTime: null,
        },
      };
    });

    quotaWarningShown.current[serviceId] = false;
  }, []);

  const handleFailure = useCallback((serviceId: ServiceId, status: number, errorMessage: string, isRetrying: boolean) => {
    if (status === 429) {
      showRateLimitedToast(60);
      return;
    }

    if (isRetrying) {
      const service = services[serviceId];
      if (service.status === 'healthy') {
        showDegradedToast(SERVICE_NAMES[serviceId]);
      }
      return;
    }

    setServices((prev) => {
      const service = prev[serviceId];
      const newFailures = service.consecutiveFailures + 1;

      let newStatus: ServiceStatus = service.status;
      if (newFailures >= OFFLINE_THRESHOLD) {
        newStatus = 'offline';
      } else if (newFailures >= DEGRADED_THRESHOLD) {
        newStatus = 'degraded';
      }

      if (newStatus !== previousStatus.current[serviceId]) {
        if (newStatus === 'offline') {
          showOfflineToast(SERVICE_NAMES[serviceId]);
        } else if (newStatus === 'degraded') {
          showDegradedToast(SERVICE_NAMES[serviceId]);
        }
        previousStatus.current[serviceId] = newStatus;
      }

      return {
        ...prev,
        [serviceId]: {
          status: newStatus,
          consecutiveFailures: newFailures,
          lastError: errorMessage,
          lastErrorTime: new Date(),
        },
      };
    });
  }, [services]);

  const handleRateLimitUpdate = useCallback((serviceId: ServiceId, rateLimitInfo: RateLimitInfo) => {
    setRateLimits((prev) => ({
      ...prev,
      [serviceId]: {
        limitMinute: rateLimitInfo.limitMinute,
        remainingMinute: rateLimitInfo.remainingMinute,
        resetMinute: rateLimitInfo.resetMinute,
        limitHour: rateLimitInfo.limitHour,
        remainingHour: rateLimitInfo.remainingHour,
        resetHour: rateLimitInfo.resetHour,
      },
    }));

    if (rateLimitInfo.limitMinute && rateLimitInfo.remainingMinute != null) {
      const used = rateLimitInfo.limitMinute - rateLimitInfo.remainingMinute;
      const percentUsed = Math.round((used / rateLimitInfo.limitMinute) * 100);
      const resetSeconds = rateLimitInfo.resetMinute || 60;

      if (percentUsed >= QUOTA_WARNING_THRESHOLD && !quotaWarningShown.current[serviceId]) {
        showQuotaWarningToast(percentUsed, rateLimitInfo.remainingMinute, resetSeconds);
        quotaWarningShown.current[serviceId] = true;

        setTimeout(() => {
          quotaWarningShown.current[serviceId] = false;
        }, 60000);
      }
    }
  }, []);

  useEffect(() => {
    registerHealthCallbacks({
      onSuccess: handleSuccess,
      onFailure: handleFailure,
      onRateLimitUpdate: handleRateLimitUpdate,
    });

    return () => {
      unregisterHealthCallbacks();
    };
  }, [handleSuccess, handleFailure, handleRateLimitUpdate]);

  const isAnyServiceDegraded = Object.values(services).some(
    (s) => ['degraded', 'offline'].includes(s.status)
  );

  const isAnyServiceOffline = Object.values(services).some((s) => s.status === 'offline');

  const value: ApiHealthContextValue = {
    services,
    isAnyServiceDegraded,
    isAnyServiceOffline,
    rateLimits,
    getQuotaPercentage,
    QUOTA_WARNING_THRESHOLD,
    QUOTA_CRITICAL_THRESHOLD,
    resetServiceHealth: (serviceId: ServiceId) => {
      setServices((prev) => ({
        ...prev,
        [serviceId]: {
          status: 'healthy' as ServiceStatus,
          consecutiveFailures: 0,
          lastError: null,
          lastErrorTime: null,
        },
      }));
      previousStatus.current[serviceId] = 'healthy';
    },
  };

  return (
    <ApiHealthContext.Provider value={value}>
      {children}
    </ApiHealthContext.Provider>
  );
};

export default ApiHealthContext;
